import { lookupWaccIndustry } from "../data/wacc-industry";
import { lookupDebtSpread } from "../data/cost-of-debt-spread";
import { US_MARGINAL_TAX_RATE } from "../data/marginal-tax-rates";

const RISK_FREE_RATE = 0.047;
const EQUITY_RISK_PREMIUM = 0.05;
const US_PRIME_RATE = 0.075;
const SIZE_PREMIUM = 0.047;
const GLOBAL_DEFAULT_SPREAD = 0;

export interface WaccInput {
  industry: string;
  costOfDebt?: number;
  isPrivatelyOwned: boolean;
  hasCustomerConcentration: boolean;
  hasHighCompetition: boolean;
  hasVolatileCashFlow: boolean;
  ownerHoursPerWeek: number;
  yearsOperating: number;
  hasSustainableAdvantage: boolean;
  useMarginalTaxRate?: boolean;
  marginalTaxRate?: number;
  riskFreeRate?: number;
  waccDirectOverride?: number;
}

export interface WaccResult {
  wacc: number;
  costOfEquity: number;
  costOfDebt: number;
  afterTaxCostOfDebt: number;
  equityPct: number;
  debtPct: number;
  marginalTaxRate: number;
  details: {
    industrialCostOfEquity: number;
    sizePremium: number;
    companySpecificRisk: number;
  };
}

export function calculateWacc(input: WaccInput): WaccResult {
  const industryData = lookupWaccIndustry(input.industry);
  if (!industryData) {
    throw new Error(`Unknown WACC industry: "${input.industry}"`);
  }

  const riskFree = input.riskFreeRate ?? RISK_FREE_RATE;

  const industrialCostOfEquity =
    riskFree + industryData.beta * EQUITY_RISK_PREMIUM;

  const sizePremium = SIZE_PREMIUM;

  let companySpecificRisk = 0;
  if (input.isPrivatelyOwned) companySpecificRisk += 0.03;
  if (input.hasCustomerConcentration) companySpecificRisk += 0.03;
  if (input.hasHighCompetition) companySpecificRisk += 0.03;
  if (input.hasVolatileCashFlow) companySpecificRisk += 0.03;
  if (input.ownerHoursPerWeek <= 20) companySpecificRisk -= 0.02;
  if (input.yearsOperating >= 10) companySpecificRisk -= 0.01;
  if (input.hasSustainableAdvantage) companySpecificRisk -= 0.03;

  const costOfEquity =
    industrialCostOfEquity + sizePremium + companySpecificRisk;

  const useMarginalTax = input.useMarginalTaxRate ?? true;
  const marginalTaxRate = input.marginalTaxRate ?? US_MARGINAL_TAX_RATE;

  let costOfDebt: number;
  if (input.costOfDebt && input.costOfDebt > 0) {
    costOfDebt = input.costOfDebt;
  } else {
    const spread = lookupDebtSpread(industryData.stdDevInStock);
    costOfDebt = US_PRIME_RATE + spread + GLOBAL_DEFAULT_SPREAD;
  }

  const afterTaxCostOfDebt = useMarginalTax
    ? costOfDebt * (1 - marginalTaxRate)
    : costOfDebt * (1 - industryData.taxRate);

  const equityPct = industryData.equityPct;
  const debtPct = industryData.debtPct;

  const wacc = equityPct * costOfEquity + debtPct * afterTaxCostOfDebt;

  return {
    wacc,
    costOfEquity,
    costOfDebt,
    afterTaxCostOfDebt,
    equityPct,
    debtPct,
    marginalTaxRate,
    details: {
      industrialCostOfEquity,
      sizePremium,
      companySpecificRisk,
    },
  };
}
